import _ from 'lodash';
import dayjs from 'dayjs';
import CryptoJS from 'crypto-js';
import envConfig from './envConfig';
import {
  uploadHistoryStatus,
  fareboxCollectionSubmissionStatus,
  fareboxCollectionSubmissionAttachmentFileTypes,
  storageFileProcessesStatus,
  incidentalsSubmissionStatus,
  specialServiceStatus,
  paratransitServiceStatus,
  paratransitServiceRouteStatus
} from './utils'

export const encryptString = (text) => {
  const iv = CryptoJS.lib.WordArray.random(16);
  const keyWordArray = CryptoJS.enc.Utf8.parse(envConfig.EncryptionKey);
  const encrypted = CryptoJS.AES.encrypt(text, keyWordArray, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7
  });
  const encryptedData = iv
    .concat(encrypted.ciphertext)
    .toString(CryptoJS.enc.Base64);
  return encryptedData;
};

export const animationTransition = {
  duration: 0.5,
  ease: [0.43, 0.13, 0.23, 0.96],
};

export const fadeInMotion = {
  opacity: 1,
  transition: animationTransition,
};

export const fadeOutMotion = {
  opacity: 0,
  transition: animationTransition,
};

export const toLocalTime = (date) => {
  if (date) {
    return dayjs(date).subtract(4, 'hour');
  }
  return dayjs.utc().subtract(4, 'hour')
};

export function formatNumber(number) {
  if (number) {
    return number.toLocaleString();
  }
  return '';
}

export const percentage = (n, decimalPoints, nullValueFallback) => {
  let number = n;
  if (number === null || number === undefined) {
    return nullValueFallback ?? '0';
  }
  if (number) {
    const wasNegative = number < 0;
    number = parseFloat(number.toString().replace(',', ''));
    if (wasNegative) {
      number = (-1 * number).toFixed(decimalPoints);
    } else {
      number = number.toFixed(decimalPoints);
    }
    const parts = number.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const result = parts.join('.');
    if (wasNegative) {
      return `-${result}%`;
    }
    return `${result}%`;
  }
  return nullValueFallback ?? '0';
}

export const thousand = (n, decimalPoints, nullValueFallback) => {
  let number = n;
  if (number === null || number === undefined) {
    return nullValueFallback ?? '-';
  }
  if (number) {
    const wasNegative = number < 0;

    let tempDecimal = 0;
    if (decimalPoints) {
      tempDecimal = decimalPoints;
    }
    number = parseFloat(number.toString().replace(',', ''));
    if (!decimalPoints && number % 1 === 0) {
      tempDecimal = 0;
    } else if (!decimalPoints) {
      tempDecimal = 2;
    }
    if (wasNegative) {
      number = (-1 * number).toFixed(tempDecimal);
    } else {
      number = number.toFixed(tempDecimal);
    }
    const parts = number.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const result = parts.join('.');
    if (wasNegative) {
      return `-${result}`;
    }
    return `${result}`;
  }
  return nullValueFallback ?? '0';
};

export const thousandAllowZero = (n, decimalPoints, nullValueFallback) => {
  let number = n;
  if (number === null || number === undefined) {
    return nullValueFallback ?? '0';
  }
  if (number) {
    const wasNegative = number < 0;

    let tempDecimal = 0;
    if (decimalPoints) {
      tempDecimal = decimalPoints;
    }
    number = parseFloat(number.toString().replace(',', ''));
    if (!decimalPoints && number % 1 === 0) {
      tempDecimal = 0;
    } else if (!decimalPoints) {
      tempDecimal = 2;
    }
    if (wasNegative) {
      number = (-1 * number).toFixed(tempDecimal);
    } else {
      number = number.toFixed(tempDecimal);
    }
    const parts = number.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const result = parts.join('.');
    if (wasNegative) {
      return `-${result}`;
    }
    return `${result}`;
  }
  return nullValueFallback ?? '0';
};

export const toCurrency = (number, nullValueFallback) => {
  try {
    if (!number) {
      return nullValueFallback ?? '-';
    }
    const wasNegative = number < 0;
    let fixed = 0;
    if (wasNegative) {
      fixed = (-1 * number).toFixed(2);
    } else {
      fixed = number.toFixed(2);
    }
    if (wasNegative) {
      return `-$${thousand(fixed, 2, nullValueFallback)}`;
    }
    return `$${thousand(fixed, 2, nullValueFallback)}`;
  } catch (error) {
    return nullValueFallback ?? '-';
  }
};

export const uuidv4 = () =>
  '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
    (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
  )

export const uploadHistoryStatusToString = (status) => {
  const filtered = _.filter(uploadHistoryStatus, (s) => s.id === status)
  if (filtered?.length > 0) {
    return filtered[0].name;
  }
  return '';
};

export const fareboxCollectionSubmissionStatusToString = (status) => {
  const filtered = _.filter(fareboxCollectionSubmissionStatus, (s) => s.id === status)
  if (filtered?.length > 0) {
    return filtered[0].name;
  }
  return '';
};

export const fareboxCollectionSubmissionAttachmentFileTypesToString = (type) => {
  const filtered = _.filter(fareboxCollectionSubmissionAttachmentFileTypes, (s) => s.id === type)
  if (filtered?.length > 0) {
    return filtered[0].name;
  }
  return '';
};

export const storageFileProcessesStatusToString = (status) => {
  const filtered = _.filter(storageFileProcessesStatus, (s) => s.id === status)
  if (filtered?.length > 0) {
    return filtered[0].name;
  }
  return '';
}

export const incidentalsSubmissionStatusToString = (status) => {
  const filtered = _.filter(incidentalsSubmissionStatus, (s) => s.id === status)
  if (filtered?.length > 0) {
    return filtered[0].name;
  }
  return '';
};

export const specialServiceStatusToString = (status) => {
  const filtered = _.filter(specialServiceStatus, (s) => s.id === status)
  if (filtered?.length > 0) {
    return filtered[0].name;
  }
  return '';
};

export const paratransitServiceStatusToString = (status) => {
  const filtered = _.filter(paratransitServiceStatus, (s) => s.id === status)
  if (filtered?.length > 0) {
    return filtered[0].name;
  }
  return '';
};

export const paratransitServiceRouteStatusToString = (status) => {
  const filtered = _.filter(paratransitServiceRouteStatus, (s) => s.id === status)
  if (filtered?.length > 0) {
    return filtered[0].name;
  }
  return '';
}

export const paratransitServiceRouteStatusToColor = (status) => {
  const filtered = _.filter(paratransitServiceRouteStatus, (s) => s.id === status)
  if (filtered?.length > 0) {
    return filtered[0].color;
  }
  return '';
}

export const specialServiceStatusToColor = (status) => {
  const filtered = _.filter(specialServiceStatus, (s) => s.id === status)
  if (filtered?.length > 0) {
    return filtered[0].color;
  }
  return '';
};

export const paratransitServiceStatusToColor = (status) => {
  const filtered = _.filter(paratransitServiceStatus, (s) => s.id === status)
  if (filtered?.length > 0) {
    return filtered[0].color;
  }
  return '';
};

export const localizeSpecialServiceDates = (s) => ({
  ...s,
  date: dayjs(s?.date).add(-4, 'hour').format('YYYY-MM-DDTHH:mm:ss'),
  startDate: s?.startDate ? dayjs(s?.startDate).add(-4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null,
  endDate: s?.endDate ? dayjs(s?.endDate).add(-4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null,
  actEmployeeSignedDate: s?.actEmployeeSignedDate ? dayjs(s?.actEmployeeSignedDate).add(-4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null,
  firstTransitEmployeeSignedDate: s?.firstTransitEmployeeSignedDate ? dayjs(s?.firstTransitEmployeeSignedDate).add(-4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null,
});

export const localizeParatransitServiceDates = (s) => ({
  ...s,
  date: dayjs(s?.date).add(-4, 'hour').format('YYYY-MM-DDTHH:mm:ss'),
  garageDepartureTime: s?.garageDepartureTime ? dayjs(s?.garageDepartureTime).add(-4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null,
  garageArrivalTime: s?.garageArrivalTime ? dayjs(s?.garageArrivalTime).add(-4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null,
  routes: _.map(s.routes, (r) => ({
    ...r,
    coordinatedPickupTime: r?.coordinatedPickupTime ? dayjs(r?.coordinatedPickupTime).add(-4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null,
    pickupArrivalTime: r?.pickupArrivalTime ? dayjs(r?.pickupArrivalTime).add(-4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null,
    destinationArrivalTime: r?.destinationArrivalTime ? dayjs(r?.destinationArrivalTime).add(-4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null
  }))
});

export const localizeParatransitRouteDates = (r) => ({
  ...r,
  coordinatedPickupTime: r?.coordinatedPickupTime ? dayjs(r?.coordinatedPickupTime).add(-4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null,
  pickupArrivalTime: r?.pickupArrivalTime ? dayjs(r?.pickupArrivalTime).add(-4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null,
  destinationArrivalTime: r?.destinationArrivalTime ? dayjs(r?.destinationArrivalTime).add(-4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null
});

export const invoiceVersionToString = (v) => {
  if (!v) {
    return '';
  }
  const temp = '' + v;
  const year = temp.substring(0, 4);
  const month = temp.substring(4, 6);
  const day = temp.substring(6, 8);
  const time = temp.substring(8, temp.length);
  const result = `${year}-${month}-${day}.${time}`;
  if (result.endsWith('.')) {
    return result.substring(0, result.length - 1);
  }
  return result;
};

export const getVersionsForSelect = (versions, t, disableSelectedVersion, selectedVersion) => {
  const groups = [];
  let options = [];
  const currentVersions = _.filter(versions, (v) => v.isCurrentVersion);
  const notCurrentVersions = _.filter(versions, (v) => !v.isCurrentVersion);
  if (currentVersions.length > 0) {
    groups.push({
        name: t('latest_version'),
        options: _.map(currentVersions, (v) => ({
        key: v.version,
        value: v.version,
        text: invoiceVersionToString(v.version),
        disabled: (disableSelectedVersion ? (v.version === selectedVersion) : false)
      }))
    });
    groups.push({
        name: t('other_versions'),
        options: _.map(notCurrentVersions, (v) => ({
        key: v.version,
        value: v.version,
        text: invoiceVersionToString(v.version),
        disabled: (disableSelectedVersion ? (v.version === selectedVersion) : false)
      }))
    });
  } else {
    options = _.map(notCurrentVersions, (v) => ({
      key: v.version,
      value: v.version,
      text: invoiceVersionToString(v.version),
      disabled: (disableSelectedVersion ? (v.version === selectedVersion) : false)
    }));
  }
  return { groups, options };
}

export const isDateSameOrBetween = (date, afterDate, beforeDate, unitType) => {
  const currentDate = dayjs(date);
  const after = dayjs(afterDate);
  const before = dayjs(beforeDate);
  if ((currentDate.isSame(after, unitType) || currentDate.isAfter(after, unitType)) && (currentDate.isSame(before, unitType) || currentDate.isBefore(before, unitType))) {
    return true;
  }
  return false;
};

export const isDateBetween = (date, afterDate, beforeDate, unitType) => {
  const currentDate = dayjs(date);
  const after = dayjs(afterDate);
  const before = dayjs(beforeDate);
  if (currentDate.isAfter(after, unitType) && currentDate.isBefore(before, unitType)) {
    return true;
  }
  return false;
};

Number.prototype.roundTo = function (d) {
  return parseFloat(this.toFixed(d));
};

export const convertKmhToMph = (kmh) => {
  return (kmh * 0.621371).roundTo(0);
}

export const componentToHex = (c) => {
  const hex = c.toString(16);
  return hex.length === 1 ? '0' + hex : hex;
}

export const rgbToHex = (r, g, b) => {
  return '#' + componentToHex(r) + componentToHex(g) + componentToHex(b);
}
